import { create } from 'zustand';

import {
  uploadFile,
  type UploadFileOptions,
  type UploadFileResult,
  type UploadStatus,
} from './upload-file';

export interface OwnedUploadProgress {
  /** 0–1, rounded to hundredths so a bar re-renders per percent, not per byte. */
  ratio: number;
  status: UploadStatus | null;
}

/** One upload in a scope listing ({@link OwnedUploads.useScope}). */
export interface OwnedUploadEntry extends OwnedUploadProgress {
  key: string;
}

/** What an owned upload's run reports through — the `uploadFile` callbacks. */
export interface OwnedUploadRun {
  signal: AbortSignal;
  onProgress: (ratio: number) => void;
  onStatus: (status: UploadStatus) => void;
}

const NO_ENTRIES: OwnedUploadEntry[] = [];

/**
 * UPLOADS OWNED AT APP LEVEL, not by the component that shows them.
 *
 * The upload is started, cancelled and tracked here, so what happens to the
 * component that shows it — hidden by a freeze boundary (React's `<Activity>`
 * tears a hidden subtree's effects down), re-rendered, unmounted and mounted
 * again — cannot stop it, and a component that comes back reads where the
 * upload is now. Starting a key that is already uploading joins that upload
 * instead of sending the bytes twice. Only {@link cancel} / {@link cancelScope}
 * stop one: the caller decides what "the user left" means, never an unmount
 * cleanup.
 *
 * Every key is its own upload, so any number run IN PARALLEL — several files
 * picked at once, an audio file next to a document, two decks at a time — each
 * with its own progress and its own cancel. Keys are `kind:…` strings; a
 * shared prefix is a scope (`useScope('doc:deck-1:')` lists that deck's
 * documents in flight, `cancelScope` stops them).
 *
 * The run is passed to `start`, so an instance carries no transfer stack;
 * {@link ownedUploadFile} is the `uploadFile` run every upload in the apps uses.
 */
export function createOwnedUploads() {
  const store = create<{ uploads: Record<string, OwnedUploadProgress> }>(() => ({
    uploads: {},
  }));
  const inFlight = new Map<
    string,
    { controller: AbortController; done: Promise<unknown> }
  >();

  const patch = (key: string, next: Partial<OwnedUploadProgress>) =>
    store.setState((state) => {
      const current = state.uploads[key];
      if (!current) return state;
      return { uploads: { ...state.uploads, [key]: { ...current, ...next } } };
    });

  const drop = (key: string) =>
    store.setState((state) => {
      if (!(key in state.uploads)) return state;
      const { [key]: _done, ...rest } = state.uploads;
      return { uploads: rest };
    });

  /** Run `run` as the upload for `key`, or join the one already running. */
  function start<T>(key: string, run: (ctx: OwnedUploadRun) => Promise<T>): Promise<T> {
    const running = inFlight.get(key);
    if (running) return running.done as Promise<T>;

    const controller = new AbortController();
    store.setState((state) => ({
      uploads: { ...state.uploads, [key]: { ratio: 0, status: null } },
    }));
    const done = (async () => {
      try {
        return await run({
          signal: controller.signal,
          onProgress: (ratio) => patch(key, { ratio: Math.round(ratio * 100) / 100 }),
          onStatus: (status) => patch(key, { status }),
        });
      } finally {
        inFlight.delete(key);
        drop(key);
      }
    })();
    inFlight.set(key, { controller, done });
    return done;
  }

  /** Whether `key` is uploading right now. */
  function isUploading(key: string): boolean {
    return inFlight.has(key);
  }

  /** Stop the upload for `key` — every one, when no key is named. */
  function cancel(key?: string): void {
    if (key !== undefined) {
      inFlight.get(key)?.controller.abort();
      return;
    }
    for (const { controller } of inFlight.values()) controller.abort();
  }

  /** Stop every upload whose key starts with `prefix`. */
  function cancelScope(prefix: string): void {
    for (const [key, { controller }] of inFlight) {
      if (key.startsWith(prefix)) controller.abort();
    }
  }

  /** How far `key`'s upload has got, or `null` when it is not uploading. */
  function progressOf(key: string | null | undefined): OwnedUploadProgress | null {
    return key ? (store.getState().uploads[key] ?? null) : null;
  }

  /** {@link progressOf}, re-rendering as it moves. */
  function useProgress(key: string | null | undefined): OwnedUploadProgress | null {
    return store((state) => (key ? (state.uploads[key] ?? null) : null));
  }

  // One cached listing per prefix, so `useScope` keeps its identity while
  // nothing in that scope moved (a selector must not return a fresh array).
  const scopeCache = new Map<
    string,
    { uploads: Record<string, OwnedUploadProgress>; entries: OwnedUploadEntry[] }
  >();
  function scopeOf(prefix: string): OwnedUploadEntry[] {
    const { uploads } = store.getState();
    const cached = scopeCache.get(prefix);
    if (cached?.uploads === uploads) return cached.entries;
    const entries = Object.keys(uploads)
      .filter((key) => key.startsWith(prefix))
      .sort()
      .map((key) => ({ key, ...uploads[key] }));
    const prev = cached?.entries;
    const same =
      prev !== undefined &&
      prev.length === entries.length &&
      prev.every(
        (entry, i) =>
          entry.key === entries[i].key &&
          entry.ratio === entries[i].ratio &&
          entry.status === entries[i].status,
      );
    const next = same ? prev : entries.length ? entries : NO_ENTRIES;
    scopeCache.set(prefix, { uploads, entries: next });
    return next;
  }

  /** Every upload in flight under `prefix`, by key — parallel uploads in one list. */
  function useScope(prefix: string): OwnedUploadEntry[] {
    return store(() => scopeOf(prefix));
  }

  return {
    start,
    isUploading,
    cancel,
    cancelScope,
    progressOf,
    useProgress,
    scopeOf,
    useScope,
    store,
  };
}

export type OwnedUploads = ReturnType<typeof createOwnedUploads>;

/**
 * THE app's upload owner: every upload either client makes goes through it
 * (`ownedUploadFile`), so any screen can read or cancel any upload by its key.
 */
export const appUploads = createOwnedUploads();

let nextUploadId = 0;
const uploadIds = new WeakMap<object, string>();

/**
 * A stable id for the object being uploaded (a web `File`/`Blob`, a picked
 * document's info): the same object always gets the same id, so picking it
 * again while it uploads joins that upload, and an owner key can be built
 * where the caller has no id of its own.
 */
export function uploadIdOf(object: object): string {
  let id = uploadIds.get(object);
  if (!id) {
    nextUploadId += 1;
    id = `u${nextUploadId}`;
    uploadIds.set(object, id);
  }
  return id;
}

/** A signal that aborts when either does (`AbortSignal.any` is not on Hermes). */
export function eitherSignal(a: AbortSignal, b?: AbortSignal): AbortSignal {
  if (!b) return a;
  if (a.aborted || b.aborted) {
    const controller = new AbortController();
    controller.abort();
    return controller.signal;
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  a.addEventListener('abort', abort, { once: true });
  b.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

/**
 * `uploadFile`, owned: the upload runs under `key` in `owner` (the app-wide
 * {@link appUploads} by default), so it outlives the component that started
 * it and anyone can read its progress or cancel it by key. The caller's own
 * `signal`, `onProgress` and `onStatus` keep working alongside.
 *
 * A key already uploading is JOINED: the caller gets that upload's result,
 * and reads its progress from the owner, since its own callbacks are not the
 * ones the running upload was started with.
 */
export function ownedUploadFile(
  key: string,
  options: UploadFileOptions,
  owner: OwnedUploads = appUploads,
): Promise<UploadFileResult> {
  return owner.start(key, (run) =>
    uploadFile({
      ...options,
      signal: eitherSignal(run.signal, options.signal),
      onProgress: (ratio) => {
        run.onProgress(ratio);
        options.onProgress?.(ratio);
      },
      onStatus: (status) => {
        run.onStatus(status);
        options.onStatus?.(status);
      },
    }),
  );
}
